import { useState } from "react";
import { Modal } from "./Modal";
import { Lead, List, ListRow, SectionHeader, StatTile } from "./ds";
import { Glyph } from "./Glyph";
import { Icon } from "../../icons";
import { st } from "../../styles.js";
import { streakLabel, todayKey } from "../../lib/format";

// v34 — spoločné popupy k dvom štatistikám (denná séria, perfektné dni). Otvárajú sa z tých istých
// čísel na dvoch miestach: zo štítkov v hlavičke na Dnes aj z dlaždíc v Prehľade → Štatistiky.
// Zo štítku sa NIKAM neprepína (žiadny presun na Prehľad), len sa otvorí toto okno.

// Perfektné dni: celkový počet, séria po sebe a podiel z aktívnych dní — jeden zdroj pravdy pre
// dlaždicu aj pre modal. `allDone` = živý stav obrazovky Dnes (server to vie ako s.perfectToday),
// `activeDays` = ak ho volajúci už počíta, nech sa čísla nerozchádzajú.
export function perfectDayStats(s, { allDone = false, activeDays } = {}) {
  const done = s.doneDays || [];
  const perfToday = allDone || !!s.perfectToday;
  const active = activeDays != null
    ? activeDays
    : done.length + (done.includes(todayKey()) || !s.streakExtendedToday ? 0 : 1);
  const perf = (s.perfectDays || 0) + (perfToday ? 1 : 0);
  const perfStreak = (s.perfectStreak || 0) + (perfToday ? 1 : 0);
  const pct = active > 0 ? Math.min(100, Math.round((100 * perf) / active)) : 0;
  // `perfToday` ide von, aby popup vedel povedať to isté, čo popup Dennej série: či sa dnešok
  // do série už zarátal (v30.3 — rovnaká logika ako `streakExtendedToday` pri sérii).
  return { perf, perfStreak, pct, activeDays: active, perfToday };
}

// Denná séria: aktuálna séria + vedľa nej najdlhšia (rekord).
export function StreakModal({ s, onClose }) {
  const cur = s.streak || 0;
  const best = s.bestStreak || 0;
  const freezes = s.streak_freezes || 0;
  const bestSub = best === 0 ? "zatiaľ žiadny rekord"
    : cur >= best ? "toto je tvoj rekord"
      : `ešte ${streakLabel(best - cur)} a prekonáš ho`;
  return (
    <Modal center title="Denná séria" icon="flame" maxWidth={360} onClose={onClose}>
      <div className="bz-statgrid2">
        <StatTile accent="var(--stat-flame)" icon={<Icon name="streak" size={24} color="currentColor" />}
          value={cur} label="Teraz" sub={s.streakExtendedToday ? "dnes už predĺžená" : "dnes ešte nepredĺžená"}
          pct={best > 0 ? 100 * Math.min(1, cur / best) : (cur > 0 ? 100 : 0)} />
        <StatTile accent="var(--stat-flame)" icon={<Icon name="level" size={24} color="currentColor" />}
          value={best} label="Najdlhšia" sub={bestSub} />
      </div>
      <div style={{ ...st.taskDesc, marginTop: 14 }}>
        Séria rastie každý deň, keď splníš aspoň jeden návyk. Preskočenie návyku ani deň, keď si
        nemal nič naplánované, ju nepreruší.
        {freezes > 0 ? (freezes === 1
          ? " Máš 1 záchranný kruh — jeden vynechaný deň ti zachráni automaticky."
          : ` Máš ${freezes} ${freezes <= 4 ? "záchranné kruhy" : "záchranných kruhov"} — vynechaný deň ti zachránia automaticky.`) : ""}
      </div>
    </Modal>
  );
}

// v29.8 — Body dňa: rozpis, za čo dnes koľko bodov padlo. Otvára sa ťuknutím na „95/95 b" v hlavičke
// na Dnes. Zdroj je VŽDY server (`day_points_rows` z /api/state) — body za splnenie sú uložený
// snapshot (náročnosť × koeficient série v momente splnenia), takže sa nedajú dopočítať na klientovi.
// Bonus za perfektný deň je zámerne AŽ pod čiarou, mimo zlomku: nie je súčasťou denného maxima,
// lebo padne až vtedy, keď je celý deň splnený.
// v36 — hlavička na Dnes ukazuje len ZÁKLAD („12/50 b"), aby bol zlomok pochopiteľný. Tento popup
// je preto jediné miesto s celou pravdou: pri návykoch ostávajú body vrátane koeficientu série
// a pod čiarou je súhrn základ → bonus za sériu → bonus za perfektný deň → spolu.
export function DayPointsModal({ s, onClose }) {
  const rows = s.day_points_rows || [];
  const done = rows.filter((r) => r.done);
  const left = rows.filter((r) => !r.done);
  const earned = s.day_points_earned || 0;
  const pot = s.day_points_potential || 0;
  const bonus = s.day_bonus || 0;
  // základ = to isté číslo, aké visí v hlavičke na Dnes (bez koeficientu série)
  const baseEarned = s.day_points_base_earned || 0;
  // koľko z dnešných bodov pridal koeficient série (server ho počíta per návyk, tu sa len odčíta)
  const streakBonus = Math.max(0, earned - baseEarned);
  const [showAll, setShowAll] = useState(false);
  // v37 — zoznam „ešte čaká" sa dlhým dňom (30+ návykov) roztiahne na tri obrazovky a súhrn
  // pod čiarou nikto neuvidí. Ukáž prvých PREVIEW a zvyšok na požiadanie.
  const PREVIEW = 5;
  const leftShown = showAll ? left : left.slice(0, PREVIEW);
  const hiddenCount = left.length - leftShown.length;

  // v30 — pod názvom ostáva séria (odkiaľ sa koeficient vzal). Pri NESPLNENOM návyku je to
  // séria, ktorú dostane až tým splnením (body vedľa sú tiež za ten ďalší tick) — preto
  // „po splnení", inak by číslo protirečilo štítku série na samotnom návyku.
  const meta = (r) => {
    if (!r.streak) return null;
    const t = `séria ${streakLabel(r.streak, r.streak_unit)}`;
    return r.done ? t : `po splnení ${t}`;
  };
  // v37 — výpočet je vidieť priamo v riadku: [základ] ×koef = výsledok. Koeficient 1,0 sa
  // stlmí (nič nenásobí, ale stĺpec musí sedieť vo všetkých riadkoch pod sebou).
  // vždy s jednou desatinou („×1,0", nie „×1") — inak stĺpec pod sebou poskakuje
  const coefTxt = (c) => `×${Number(c ?? 1).toFixed(1).replace(".", ",")}`;
  const row = (r, muted) => (
    <ListRow key={`${r.done ? "d" : "l"}${r.id}`}
      lead={<Lead size="sm" tone={muted ? "outline" : "brand"}><Glyph icon={r.icon} size={17} /></Lead>}
      title={r.label}
      meta={meta(r)}
      trailing={(
        <span className="bz-daypts__calc">
          <span className="bz-daypts__base">{r.base ?? "?"}</span>
          <span className={`bz-daypts__coef${r.coef && r.coef !== 1 ? " bz-daypts__coef--on" : ""}`}>
            {coefTxt(r.coef)}
          </span>
          <span className={muted ? "bz-daypts__val bz-daypts__val--muted" : "bz-daypts__val"}>{r.pts} b</span>
        </span>
      )} />
  );
  // súčet nad tabuľkou: ZÁKLAD a vedľa neho, čo k nemu pridali koeficienty série
  const sum = (base, total) => {
    const extra = Math.max(0, total - base);
    return extra > 0 ? `${base} + ${extra} b` : `${total} b`;
  };
  const baseDone = done.reduce((a, r) => a + (r.base || 0), 0);
  const baseLeft = left.reduce((a, r) => a + (r.base || 0), 0);
  const ptsLeft = left.reduce((a, r) => a + (r.pts || 0), 0);
  // pás nad zoznamom: koľko z denného maxima už padlo a z čoho sa to skladá.
  // Maximum zahŕňa aj bonus za perfektný deň, KTORÝ EŠTE NEPADOL (`day_bonus_potential`) —
  // inak by sa pás doplnil na 100 % skôr, než je deň naozaj hotový, a chýbajúci kúsok by
  // nemal kde byť vidieť.
  const bonusPot = Math.max(bonus, s.day_bonus_potential || 0);
  // maximá jednotlivých zložiek — legenda ukazuje „koľko z koľkých", nie len to, čo padlo:
  // základ = súčet náročností všetkého naplánovaného, séria = čo k tomu koeficienty pridajú,
  // keď sa dnes doklikne všetko (rozdiel plných a základných bodov, obe zo servera).
  const basePot = s.day_points_base_potential || 0;
  const streakPot = Math.max(streakBonus, pot - basePot);
  const totalToday = earned + bonus;
  const maxToday = pot + bonusPot;
  const pctOf = (v) => (maxToday > 0 ? (100 * Math.min(v, maxToday)) / maxToday : 0);
  const pctBase = pctOf(baseEarned);
  const pctStreak = pctOf(streakBonus);
  const pctPerfect = pctOf(bonus);
  // rebrík koeficientov chodí zo servera (db.STREAK_COEF), nezrkadlí sa v klientovi
  const ladder = s.streak_ladder || [];
  const curCoef = rows.reduce((a, r) => Math.max(a, r.coef || 1), 1);
  return (
    <Modal center title="Body dňa" icon="chart" maxWidth={380} onClose={onClose}>
      {/* v37 — vysvetlenie hneď pod hlavičkou: čísla v páse aj v riadkoch dávajú zmysel až
          vtedy, keď človek vie, čo sa k základu ešte môže pripočítať. Nahrádza poznámku, ktorá
          bola na konci okna — tam ju po dlhom zozname nikto nedočítal. */}
      {rows.length > 0 && (
        <p className="bz-daypts__intro">
          Dnes máš <b>{totalToday} z {maxToday} b</b>. Základ za naplánované návyky je{" "}
          <b>{basePot} b</b>{streakPot > 0 ? <> a pri udržaní sérií k nemu koeficienty pridajú
            až <b>+{streakPot} b</b></> : null}.{" "}
          {bonusPot > 0 ? <>Keď splníš úplne všetko, pribudne ešte bonus za perfektný deň{" "}
            <b>+{bonusPot} b</b>. </> : null}
          V hlavičke na Dnes vidíš len ten základ.
        </p>
      )}
      {rows.length > 0 && (
        <div className="bz-daypts__hero">
          <div className="bz-daypts__heroline">
            <span className="bz-daypts__herobig">{totalToday}</span>
            <span className="bz-daypts__herosub">z {maxToday} b dnes</span>
          </div>
          <div className="bz-daypts__bar" role="presentation">
            <span className="bz-daypts__bar-base" style={{ width: `${pctBase}%` }} />
            <span className="bz-daypts__bar-streak" style={{ width: `${pctStreak}%` }} />
            <span className="bz-daypts__bar-perfect" style={{ width: `${pctPerfect}%` }} />
          </div>
          <div className="bz-daypts__legend">
            <span className="bz-daypts__legenditem"><i className="bz-daypts__dot bz-daypts__dot--base" />
              Základ {baseEarned}/{basePot} b</span>
            {streakPot > 0 && (
              <span className="bz-daypts__legenditem"><i className="bz-daypts__dot bz-daypts__dot--streak" />
                Séria +{streakBonus}/{streakPot} b</span>
            )}
            {bonusPot > 0 && (
              <span className="bz-daypts__legenditem"><i className="bz-daypts__dot bz-daypts__dot--perfect" />
                {bonus > 0 ? `Perfektný deň +${bonus} b` : `Perfektný deň až +${bonusPot} b`}</span>
            )}
          </div>
        </div>
      )}
      {done.length > 0 && <>
        <SectionHeader label="Splnené" count={sum(baseDone, earned)} style={{ marginTop: 4 }} />
        <List>{done.map((r) => row(r, false))}</List>
      </>}
      {left.length > 0 && <>
        <SectionHeader label="Ešte čaká" count={sum(baseLeft, ptsLeft)} />
        <List>{leftShown.map((r) => row(r, true))}</List>
        {hiddenCount > 0 && (
          <button type="button" className="bz-daypts__more" onClick={() => setShowAll(true)}>
            + ďalších {hiddenCount} {hiddenCount <= 4 ? "návyky" : "návykov"}
          </button>
        )}
      </>}
      {rows.length === 0 && <div style={st.taskDesc}>Dnes nemáš naplánovaný žiadny návyk.</div>}
      {/* v37 — rebrík koeficientov: bez neho je „×1,6" magické číslo. Zvýraznený je stupeň,
          na ktorý používateľ práve dosiahol. */}
      {ladder.length > 0 && rows.length > 0 && (
        <div className="bz-daypts__ladder">
          <div className="bz-sectlabel">Koeficient série</div>
          <div className="bz-daypts__steps">
            {ladder.map((l, i) => (
              <div key={l.days} className={`bz-daypts__step${l.coef === curCoef ? " bz-daypts__step--on" : ""}`}>
                <span className="bz-daypts__stepbar" style={{ opacity: 0.35 + (0.65 * i) / Math.max(1, ladder.length - 1) }} />
                <span className="bz-daypts__stepdays">{l.days} d</span>
                <span className="bz-daypts__stepcoef">{coefTxt(l.coef)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* v37 — pod zoznamom ostal JEDINÝ riadok, a to len keď je čo hlásiť: bonus za perfektný
          deň. Základ, sériu aj celkový súčet povie úvodná veta a pás hore („66 z 442 b"), takže
          čokoľvek ďalšie tu už bolo len zopakované číslo. */}
      {bonus > 0 && (
        <div className="bz-daypts__bonus">
          <span><Icon name="target" size={15} color="currentColor" /> Bonus za perfektný deň</span>
          <span className="bz-daypts__val">+{bonus} b</span>
        </div>
      )}
    </Modal>
  );
}

// Perfektné dni: séria po sebe + celkový počet.
export function PerfectDaysModal({ s, allDone = false, activeDays, onClose }) {
  const p = perfectDayStats(s, { allDone, activeDays });
  return (
    <Modal center title="Perfektné dni" icon="target" maxWidth={360} onClose={onClose}>
      <div className="bz-statgrid2">
        <StatTile accent="var(--stat-growth)" icon={<Icon name="target" size={24} color="currentColor" />}
          value={p.perfStreak} label="Po sebe"
          sub={p.perfStreak === 0 ? "zatiaľ žiadna séria"
            : p.perfToday ? "dnes už predĺžená" : "dnes ešte nepredĺžená"} />
        <StatTile accent="var(--stat-growth)" icon={<Icon name="check" size={24} color="currentColor" />}
          value={p.perf} label="Celkovo"
          sub={p.activeDays > 0 ? `${p.pct} % z ${p.activeDays} aktívnych dní` : "zatiaľ žiadny aktívny deň"}
          pct={p.pct} />
      </div>
      <div style={{ ...st.taskDesc, marginTop: 14 }}>
        Perfektný deň = splnil si všetko, čo si mal v ten deň naplánované. Deň, keď si nemal
        naplánované nič (alebo si návyk preskočil), sériu nepreruší.
        {!p.perfToday && " Dnešok sa do série zatiaľ nezarátal — pripočíta sa, až keď splníš všetko, čo máš na dnes naplánované."}
      </div>
    </Modal>
  );
}
